import {
  AI_CONVERSATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  CALENDAR_EVENTS_MUTATION_SIDE_EFFECT_CONFIGS,
  ENDUSER_ELIGIBILITY_RESULTS_MUTATION_SIDE_EFFECT_CONFIGS,
  ENDUSER_MEDICATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  ENDUSER_ORDERS_MUTATION_SIDE_EFFECT_CONFIGS,
  ENDUSERS_MUTATION_SIDE_EFFECT_CONFIGS,
  FILES_MUTATION_SIDE_EFFECT_CONFIGS,
  INBOX_THREADS_MUTATION_SIDE_EFFECT_CONFIGS,
  INTEGRATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  PHONE_CALLS_MUTATION_SIDE_EFFECT_CONFIGS,
  PURCHASES_MUTATION_SIDE_EFFECT_CONFIGS,
  FORMS_MUTATION_SIDE_EFFECT_CONFIGS,
  FORM_RESPONSES_MUTATION_SIDE_EFFECT_CONFIGS,
  ORGANIZATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  PORTAL_BRANDINGS_MUTATION_SIDE_EFFECT_CONFIGS,
  SMS_MESSAGES_MUTATION_SIDE_EFFECT_CONFIGS,
  CHATS_MUTATION_SIDE_EFFECT_CONFIGS,
  MANAGED_CONTENT_RECORD_ASSIGNMENTS_MUTATION_SIDE_EFFECT_CONFIGS,
  EMAILS_MUTATION_SIDE_EFFECT_CONFIGS,
  TICKET_THREADS_MUTATION_SIDE_EFFECT_CONFIGS,
  TICKETS_MUTATION_SIDE_EFFECT_CONFIGS,
  CHAT_ROOMS_MUTATION_SIDE_EFFECT_CONFIGS,
  ENDUSER_OBSERVATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  USERS_MUTATION_SIDE_EFFECT_CONFIGS,
} from "./model-automatic-mutation-side-effect-configs";
import { TICKET_QUEUES_MUTATION_SIDE_EFFECT_CONFIGS } from "./model-automatic-mutation-side-effect-configs/ticket-queues";
import { TICKET_THREAD_COMMENTS_MUTATION_SIDE_EFFECT_CONFIGS } from "./model-automatic-mutation-side-effect-configs/ticket-thread-comments";
import type { CustomAutomaticMutationSideEffectConfigs } from "./types";

// NOTE: all models have the following mutation methods: 'createOne', 'updateOne', 'deleteOne', 'createSome'
// NOTE: all models have the following query methods: 'getOne', 'getSome', 'getByIds'

/**
 * Configuration object that defines automatic cache update side effects for mutations.
 *
 * This configuration enables automatic optimistic updates and cache synchronization when
 * mutations are performed. For each model and mutation method, you can specify which
 * query caches should be updated and how they should be updated.
 *
 * @example
 * ```typescript
 * // Structure for a model's configuration:
 * inbox_threads: {
 *   updateOne: (mutationRequest, mutationResponse) => ({
 *     // Update specific queries by their method name
 *     getOne: {
 *       request: { id: mutationResponse?.id ?? mutationRequest.id }, // Match the query
 *       updater: () => mutationResponse,                             // How to update the cache
 *     },
 *     getSome: {
 *       predicate: ({ state, queryKey }) => boolean,  // Should this query be updated?
 *       updater: (oldData, { queryKey }) => newData,  // How to update the data
 *     },
 *   }),
 *   deleteOne: (deletedId) => ({
 *     // Remove from relevant caches
 *   }),
 * }
 * ```
 *
 * @remarks
 * Each mutation method returns a function that accepts the mutation parameters
 * and returns an object mapping query method names to their update configurations.
 *
 * Update configurations can specify:
 * - `request`: Match queries by their request parameters
 * - `predicate`: Function to determine if a query should be updated based on its state
 * - `updater`: Function to transform the old cached data into new cached data
 * - `exact`: Boolean flag for strict query matching (default: true)
 *
 * The system automatically handles:
 * - Optimistic updates (using mutationRequest when mutationResponse is undefined)
 * - Filter matching (determining if updated data still matches query filters)
 * - Sorting and pagination updates
 * - List operations (upsert, remove, prepend, append)
 *
 * @see {@link AutomaticMutationSideEffectConfigs} for the TypeScript type definition
 */
export const AUTOMATIC_MUTATION_SIDE_EFFECT_CONFIGS: CustomAutomaticMutationSideEffectConfigs = {
  ai_conversations: AI_CONVERSATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  inbox_threads: INBOX_THREADS_MUTATION_SIDE_EFFECT_CONFIGS,
  enduser_eligibility_results: ENDUSER_ELIGIBILITY_RESULTS_MUTATION_SIDE_EFFECT_CONFIGS,
  enduser_orders: ENDUSER_ORDERS_MUTATION_SIDE_EFFECT_CONFIGS,
  files: FILES_MUTATION_SIDE_EFFECT_CONFIGS,
  enduser_medications: ENDUSER_MEDICATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  integrations: INTEGRATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  purchases: PURCHASES_MUTATION_SIDE_EFFECT_CONFIGS,
  endusers: ENDUSERS_MUTATION_SIDE_EFFECT_CONFIGS,
  calendar_events: CALENDAR_EVENTS_MUTATION_SIDE_EFFECT_CONFIGS,
  phone_calls: PHONE_CALLS_MUTATION_SIDE_EFFECT_CONFIGS,
  forms: FORMS_MUTATION_SIDE_EFFECT_CONFIGS,
  form_responses: FORM_RESPONSES_MUTATION_SIDE_EFFECT_CONFIGS,
  organizations: ORGANIZATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  portal_brandings: PORTAL_BRANDINGS_MUTATION_SIDE_EFFECT_CONFIGS,
  sms_messages: SMS_MESSAGES_MUTATION_SIDE_EFFECT_CONFIGS,
  chats: CHATS_MUTATION_SIDE_EFFECT_CONFIGS,
  managed_content_record_assignments: MANAGED_CONTENT_RECORD_ASSIGNMENTS_MUTATION_SIDE_EFFECT_CONFIGS,
  emails: EMAILS_MUTATION_SIDE_EFFECT_CONFIGS,
  ticket_threads: TICKET_THREADS_MUTATION_SIDE_EFFECT_CONFIGS,
  ticket_thread_comments: TICKET_THREAD_COMMENTS_MUTATION_SIDE_EFFECT_CONFIGS,
  tickets: TICKETS_MUTATION_SIDE_EFFECT_CONFIGS,
  ticket_queues: TICKET_QUEUES_MUTATION_SIDE_EFFECT_CONFIGS,
  chat_rooms: CHAT_ROOMS_MUTATION_SIDE_EFFECT_CONFIGS,
  enduser_observations: ENDUSER_OBSERVATIONS_MUTATION_SIDE_EFFECT_CONFIGS,
  users: USERS_MUTATION_SIDE_EFFECT_CONFIGS,
} as const;
